import { useCallback, useMemo, useState } from "react";

export const useCheckboxGroup = <T extends string>(allItems: readonly T[], initialSelected: readonly T[]) => {
  const [selected, setSelected] = useState<Set<T>>(() => new Set(initialSelected));

  const isSelected = useCallback((item: T) => selected.has(item), [selected]);

  const toggle = useCallback((item: T) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(item)) {
        next.delete(item);
      } else {
        next.add(item);
      }
      return next;
    });
  }, []);

  const isAllSelected = useMemo(() => allItems.every((item) => selected.has(item)), [allItems, selected]);

  const toggleAll = useCallback(() => {
    setSelected((current) => {
      const allSelected = allItems.every((item) => current.has(item));
      return allSelected ? new Set<T>() : new Set(allItems);
    });
  }, [allItems]);

  const selectAll = useCallback(() => {
    setSelected(new Set(allItems));
  }, [allItems]);

  const clearAll = useCallback(() => {
    setSelected(new Set<T>());
  }, []);

  const setMany = useCallback((items: readonly T[], shouldSelect: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      items.forEach((item) => {
        if (shouldSelect) next.add(item);
        else next.delete(item);
      });
      return next;
    });
  }, []);

  return { clearAll, isAllSelected, isSelected, selectAll, selected, setMany, toggle, toggleAll };
};
